import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, NumberInput, Select, SelectItem, Tab, TabGroup, TabList, TabPanel, TabPanels,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import { fetchStockAudit, fetchStockRecon, fetchCommitDrift, fetchInventoryChangelog, fetchDiscrepancies } from '../api/stockAudit';
import { resyncCommit } from '../api/commitDrift';
import { postStockAdjust, fetchStockAdjustments, fetchReasonCodes, fetchInvOnhand } from '../api/stockAdjust';
import { fetchOverview } from '../api/client';
import { currentUser } from '../api/auth';
import AccessDenied from '../components/AccessDenied';
import DateRangePresets from '../components/DateRangePresets';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import StoreFilter from '../components/StoreFilter';
import Pagination from '../components/Pagination';
import { usePagination } from '../lib/usePagination';
import TableSkeleton from '../components/TableSkeleton';
import { useDrilldown } from '../components/DrilldownProvider';
import Modal from '../components/Modal';
import { localYMD } from '../lib/format';
import { exportReportPdf } from '../lib/pdf';
import { t } from '../lib/i18n';
import type { OverviewPayload, StockAuditRow, StockReconRow, CommitDriftRow, InventoryChange, DiscrepancyRow, StockAdjustment, ReasonCode, User } from '../api/types';
import DrillCell from '../components/DrillCell';

function firstOfMonth(): string {
  const d = new Date();
  return localYMD(new Date(d.getFullYear(), d.getMonth(), 1));
}

function csvEsc(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(name: string, header: string[], rows: (string | number)[][]): void {
  const body = [header, ...rows].map((r) => r.map(csvEsc).join(',')).join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function causeBadge(cause: string): JSX.Element {
  const c = t.discrepancias.causes;
  if (cause === 'not-discounted') return <Badge color="red">{c.notDiscounted}</Badge>;
  if (cause === 'wrong-amount') return <Badge color="amber">{c.wrongAmount}</Badge>;
  if (cause === 'commit-only') return <Badge color="yellow">{c.commitOnly}</Badge>;
  return <Badge color="gray">{cause || '—'}</Badge>;
}

/** #trigger-design-2026-08-25 — highlight the on-hand delta: green + / red −. */
function deltaBadge(delta: number): JSX.Element {
  if (delta > 0) return <Badge color="green">+{delta}</Badge>;
  if (delta < 0) return <Badge color="red">{delta}</Badge>;
  return <span className="text-tremor-content">{delta}</span>;
}

/**
 * Task 11 — Residual after reconciliation. Any nonzero value is the
 * still-unexplained gap between what was sold and what was actually
 * decremented; that's the whole reason the row appears in "Detectadas".
 */
function residualBadge(residual: number): JSX.Element {
  if (residual > 0) return <Badge color="red">{residual}</Badge>;
  if (residual < 0) return <Badge color="amber">{residual}</Badge>;
  return <Badge color="gray">0</Badge>;
}

/**
 * Task 11 — Origen chip: who/what explains the residual. `pos` = the same
 * POS-hook failure class as the worklist tab (red); `manual` = a human
 * reconciled/explained it via a manual adjustment (amber), naming who and,
 * when given, why.
 */
function origenChip(row: DiscrepancyRow): JSX.Element {
  const tw = t.discrepancias;
  if (row.attribution === 'manual') {
    const label = row.manual_by ? `${tw.origenManual} de ${row.manual_by}` : tw.origenManual;
    return (
      <div className="space-y-0.5">
        <Badge color="amber">{label}</Badge>
        {row.reason && <div className="text-xs text-tremor-content">{row.reason}</div>}
      </div>
    );
  }
  return <Badge color="red">{tw.origenPos}</Badge>;
}

export default function Discrepancias() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <DiscrepanciasInner user={user} /> : <Navigate to="/login" replace />;
}

function DiscrepanciasInner({ user }: { user: User }) {
  const authorized =
    user.role === 'owner' || user.role === 'manager' || (user.permissions ?? []).includes('view:stock-audit');
  // Movimientos de existencia (#trigger design 2026-08-25) — an additional,
  // narrower gate layered on top of `authorized`: the tab stays hidden until
  // the customer has the `inv` WRITE-trigger capture registered
  // (features.invchg_enabled). Same idiom as Service's DVI sub-tab
  // (dvi_enabled) — an off-module never shows a dead tab.
  const canChangelog = !!user.features?.invchg_enabled;

  const [overview, setOverview] = useState<OverviewPayload | null>(null);
  // Worklist filters.
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(() => localYMD());
  const [sid, setSid] = useState<number | null>(null);
  const [cause, setCause] = useState('');
  const [rows, setRows] = useState<StockAuditRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Ticket detail comes from the app-wide drill-down stack (JCD-59), so a
  // part clicked inside it nests instead of leaving this page.
  const { open: openDrill } = useDrilldown();

  // Reconciliación tab.
  const [reconSid, setReconSid] = useState<number | null>(null);
  const [reconDate, setReconDate] = useState(() => localYMD());
  const [reconRows, setReconRows] = useState<StockReconRow[] | null>(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconErr, setReconErr] = useState<string | null>(null);

  // Comprometido (commit-drift) tab.
  const [commitSid, setCommitSid] = useState<number | null>(null);
  const [commitRows, setCommitRows] = useState<CommitDriftRow[] | null>(null);
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitErr, setCommitErr] = useState<string | null>(null);
  const [commitExpanded, setCommitExpanded] = useState<string | null>(null);
  const [confirmResync, setConfirmResync] = useState<{ sid: number; tnum: number; part: string; multi: boolean } | null>(null);
  const [healing, setHealing] = useState(false);

  // Movimientos de existencia tab — on-hand change log (trigger capture).
  const [chgFrom, setChgFrom] = useState(() => localYMD());
  const [chgTo, setChgTo] = useState(() => localYMD());
  const [chgSid, setChgSid] = useState<number | null>(null);
  const [chgLine, setChgLine] = useState('');
  const [chgPart, setChgPart] = useState('');
  const [chgRows, setChgRows] = useState<InventoryChange[] | null>(null);
  const [chgLoading, setChgLoading] = useState(false);
  const [chgErr, setChgErr] = useState<string | null>(null);

  // Detectadas tab (Task 11) — reconciled discrepancy residuals from
  // GET /api/discrepancies: sold vs. actually-decremented, already resolved
  // server-side to a cause + an attribution (pos hook failure vs. a manual
  // adjustment).
  const [detSid, setDetSid] = useState<number | null>(null);
  const [detRows, setDetRows] = useState<DiscrepancyRow[] | null>(null);
  const [detLoading, setDetLoading] = useState(false);
  const [detErr, setDetErr] = useState<string | null>(null);
  const [detExpanded, setDetExpanded] = useState<string | null>(null);

  // Ajustar existencia (Task 7, #209 Half B) — per-row action off a
  // Detectadas residual + a standalone panel, both funneling into the same
  // confirm modal/submit. onHand/residual are null for the standalone
  // panel's free-form target (no known baseline to suggest against).
  const [adjustTarget, setAdjustTarget] = useState<{
    sid: number; line: string; part: string;
    onHand: number | null; residual: number | null; suggested: number;
  } | null>(null);
  const [adjustQty, setAdjustQty] = useState(0);
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustErr, setAdjustErr] = useState<string | null>(null);
  const [adjustSuccessMsg, setAdjustSuccessMsg] = useState<string | null>(null);
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);

  // Standalone "Ajuste de existencia" panel — its own store/line/part/qty/
  // reason inputs, funneled into adjustTarget/adjustQty/adjustReason above
  // when its own "Ajustar" button is clicked (same confirm modal + submit).
  const [panelSid, setPanelSid] = useState<number | null>(null);
  const [panelLine, setPanelLine] = useState('');
  const [panelPart, setPanelPart] = useState('');
  const [panelQty, setPanelQty] = useState(0);
  const [panelReason, setPanelReason] = useState('');

  // Ajustes de existencia tab (Task 7, B5) — transparency log from
  // GET /api/stock-adjustments. Gated owner-only (adjust:post — see
  // canViewAdjustments below), matching the server's gate exactly (user
  // decision, 2026-08-31, superseding the original "visible to all
  // authenticated users" spec). No per-store filter in this first cut
  // (unlike the other tabs) — deliberately, to avoid a second "Todas las
  // tiendas" StoreFilter landing after the
  // Movimientos de existencia tab's own, which would break that tab's
  // existing "last StoreFilter button in DOM order" test idiom; the log
  // stays a single unfiltered feed across all stores.
  const [adjRows, setAdjRows] = useState<StockAdjustment[] | null>(null);
  // Client-side pagers for the flat tables (state arrays → stable refs).
  const pgAudit = usePagination(rows);
  const pgRecon = usePagination(reconRows);
  const pgChg = usePagination(chgRows);
  const pgAdj = usePagination(adjRows);
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjErr, setAdjErr] = useState<string | null>(null);

  useEffect(() => {
    if (!authorized) return;
    let alive = true;
    fetchOverview(user.role)
      .then((p) => { if (alive) setOverview(p); })
      .catch(() => { /* StoreFilter hides gracefully */ });
    return () => { alive = false; };
  }, [authorized, user.role]);

  const load = () => {
    if (!authorized) return;
    let alive = true;
    setErr(null);
    setLoading(true);
    fetchStockAudit({ from, to, sid: sid ?? undefined, cause: cause || undefined, miss: true })
      .then((r) => { if (alive) setRows(r); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(() => { load(); }, [authorized, from, to, sid, cause]);

  const runRecon = () => {
    if (reconSid == null) return;
    setReconErr(null);
    setReconLoading(true);
    fetchStockRecon(reconSid, reconDate)
      .then((r) => setReconRows(r))
      .catch((e: Error) => setReconErr(e.message ?? String(e)))
      .finally(() => setReconLoading(false));
  };

  const runCommitDrift = () => {
    setCommitErr(null);
    setCommitLoading(true);
    fetchCommitDrift(commitSid ?? 0)
      .then((r) => setCommitRows(r))
      .catch((e: Error) => setCommitErr(e.message ?? String(e)))
      .finally(() => setCommitLoading(false));
  };

  // Heal is doubly gated client-side too: writes enabled AND commit:resync.
  // The server authorizes via Has() = role-defaults ∪ per-user overrides, and
  // /api/me only emits the *override* list — so an owner (who holds the perm by
  // role default, not as an override) must be recognized by role here too.
  const canHeal = !!user.features?.writes_enabled &&
    (user.role === 'owner' || (user.permissions ?? []).includes('commit:resync'));
  // Same double-gate shape as canHeal, for the stock-adjust write
  // (POST /api/stock-adjust, owner-only adjust:post by default — Global
  // Constraints: "the most consequential inventory write in the product").
  const canAdjust = !!user.features?.writes_enabled &&
    (user.role === 'owner' || (user.permissions ?? []).includes('adjust:post'));
  // Ajustes de existencia tab visibility (user decision, 2026-08-31): same
  // role/permission check as canAdjust (adjust:post, owner-only by default)
  // but deliberately WITHOUT the writes_enabled gate — viewing the
  // transparency log shouldn't require writes to be turned on, unlike
  // actually posting an adjustment. Matches the server's GET
  // /api/stock-adjustments gate exactly.
  const canViewAdjustments = user.role === 'owner' || (user.permissions ?? []).includes('adjust:post');
  const doResync = () => {
    if (!confirmResync) return;
    setHealing(true);
    resyncCommit(confirmResync.sid, confirmResync.tnum)
      .then(() => { setConfirmResync(null); runCommitDrift(); })
      .catch((e: Error) => setCommitErr(e.message ?? String(e)))
      .finally(() => setHealing(false));
  };

  const loadChangelog = () => {
    if (!authorized || !canChangelog) return;
    setChgErr(null);
    setChgLoading(true);
    fetchInventoryChangelog({
      from: chgFrom, to: chgTo, sid: chgSid ?? undefined,
      line: chgLine || undefined, part: chgPart || undefined,
    })
      .then((r) => setChgRows(r))
      .catch((e: Error) => setChgErr(e.message ?? String(e)))
      .finally(() => setChgLoading(false));
  };
  useEffect(() => { loadChangelog(); }, [authorized, canChangelog, chgFrom, chgTo, chgSid, chgLine, chgPart]);

  const runDetected = () => {
    setDetErr(null);
    setDetLoading(true);
    fetchDiscrepancies(detSid ?? undefined)
      .then((r) => setDetRows(r))
      .catch((e: Error) => setDetErr(e.message ?? String(e)))
      .finally(() => setDetLoading(false));
  };

  // Reason-code dropdown source for the adjust form — loaded once when the
  // user can actually adjust (a view-only user never sees the form, so
  // never needs it). Task 0 recon found this site's ReasonCode master
  // carries zero CodeType="I" rows today, so this typically resolves to []
  // and both the per-row and standalone forms fall back to free text.
  useEffect(() => {
    if (!canAdjust) return;
    let alive = true;
    fetchReasonCodes()
      .then((codes) => { if (alive) setReasonCodes(codes); })
      .catch(() => { /* free-text fallback already handles this */ });
    return () => { alive = false; };
  }, [canAdjust]);

  const loadAdjustments = () => {
    if (!canViewAdjustments) return;
    setAdjErr(null);
    setAdjLoading(true);
    fetchStockAdjustments()
      .then((r) => setAdjRows(r))
      .catch((e: Error) => setAdjErr(e.message ?? String(e)))
      .finally(() => setAdjLoading(false));
  };
  useEffect(() => { loadAdjustments(); }, [canViewAdjustments]);

  // Transient success flash after a submit — cleared automatically so it
  // doesn't linger once the admin moves on.
  useEffect(() => {
    if (!adjustSuccessMsg) return;
    const id = setTimeout(() => setAdjustSuccessMsg(null), 4000);
    return () => clearTimeout(id);
  }, [adjustSuccessMsg]);

  // Submit for BOTH the per-row action and the standalone panel — they only
  // differ in how adjustTarget/adjustQty/adjustReason got populated. Success
  // closes the modal, refreshes Detectadas (the row's residual clears once
  // the next ingest tick's closeLoop reads this adjustment's audit row) and
  // reloads the transparency log.
  const doAdjust = () => {
    if (!adjustTarget) return;
    setAdjusting(true);
    setAdjustErr(null);
    postStockAdjust(adjustTarget.sid, adjustTarget.line, adjustTarget.part, adjustQty, adjustReason)
      .then(() => {
        setAdjustTarget(null);
        setAdjustSuccessMsg(tw.ajusteSuccess);
        runDetected();
        loadAdjustments();
      })
      .catch((e: Error) => setAdjustErr(e.message ?? String(e)))
      .finally(() => setAdjusting(false));
  };

  const stores = overview?.stores ?? [];
  const tw = t.discrepancias;

  const uncaptured = useMemo(
    () => (reconRows ?? []).filter((r) => !r.captured).length,
    [reconRows],
  );

  if (!authorized) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 px-6 py-6"><AccessDenied user={user} /></main>
      </div>
    );
  }

  const exportPdf = () => {
    if (!rows || rows.length === 0) return;
    exportReportPdf({
      title: tw.title,
      meta: [`${tw.filters.from}: ${from} — ${tw.filters.to}: ${to}`],
      printedBy: user.username,
      filename: 'discrepancias.pdf',
      orientation: 'landscape',
      columns: [tw.cols.date, tw.cols.store, tw.cols.ticket, tw.cols.part, tw.cols.qty,
        tw.cols.instkBefore, tw.cols.instkAfter, tw.cols.expected, tw.cols.cause, tw.cols.cashier, tw.cols.seller],
      numericColumns: [4, 5, 6, 7],
      rows: rows.map((r) => [
        `${r.cdate} ${r.ctime}`, String(r.sid), String(r.tnum), `${r.line}/${r.part}`,
        r.qtysold, r.instk_before, r.instk_after, r.expected_after, r.cause, String(r.tkt_id), r.slid,
      ]),
    });
  };

  const exportCsv = () => {
    if (!rows) return;
    downloadCsv(
      'discrepancias.csv',
      ['fecha', 'hora', 'tienda', 'ticket', 'linea', 'parte', 'cantidad',
        'instk_antes', 'instk_despues', 'esperado', 'commit_antes', 'commit_despues',
        'onord', 'causa', 'capturista', 'vendedor'],
      rows.map((r) => [
        r.cdate, r.ctime, r.sid, r.tnum, r.line, r.part, r.qtysold,
        r.instk_before, r.instk_after, r.expected_after, r.commitqty_before, r.commitqty_after,
        r.onord, r.cause, r.tkt_id, r.slid,
      ]),
    );
  };

  const exportChangelogPdf = () => {
    if (!chgRows || chgRows.length === 0) return;
    exportReportPdf({
      title: tw.tabs.changelog,
      meta: [`${tw.filters.from}: ${chgFrom} — ${tw.filters.to}: ${chgTo}`],
      printedBy: user.username,
      filename: 'movimientos-existencia.pdf',
      orientation: 'landscape',
      columns: [tw.changelogCols.date, tw.changelogCols.time, tw.changelogCols.store, tw.changelogCols.line,
        tw.changelogCols.part, tw.changelogCols.instk, tw.changelogCols.commitqty, tw.changelogCols.delta, tw.changelogCols.userid],
      numericColumns: [5, 6, 7],
      rows: chgRows.map((r) => [
        r.cdate, r.ctime, String(r.sid), r.line, r.part, r.instk, r.commitqty, r.delta, r.userid || '—',
      ]),
    });
  };

  const exportChangelogCsv = () => {
    if (!chgRows) return;
    downloadCsv(
      'movimientos-existencia.csv',
      ['fecha', 'hora', 'tienda', 'linea', 'parte', 'existencia', 'comprometido', 'delta',
        'en_orden', 'pendiente', 'costo', 'usuario'],
      chgRows.map((r) => [
        r.cdate, r.ctime, r.sid, r.line, r.part, r.instk, r.commitqty, r.delta, r.onord, r.bkoqty, r.cost1 ?? '', r.userid,
      ]),
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{tw.title}</Title>
        <Text className="text-tremor-content text-sm">{tw.subtitle}</Text>

        <TabGroup>
          {/* TabList's children are strictly typed ReactElement[] | ReactElement
              (unlike TabPanels' looser ReactNode) — `{cond && <Tab/>}` (typed
              `false | ReactElement`) doesn't satisfy that, so the optional 4th
              tab is spliced into a plain array instead. See Inventory.tsx's
              "two variants" comment for the sibling case of this same
              constraint. */}
          <TabList>
            {[
              <Tab key="worklist">{tw.tabs.worklist}</Tab>,
              <Tab key="recon">{tw.tabs.recon}</Tab>,
              <Tab key="commit">{tw.tabs.commit}</Tab>,
              <Tab key="detectadas">{tw.detectadasTab}</Tab>,
              ...(canChangelog ? [<Tab key="changelog">{tw.tabs.changelog}</Tab>] : []),
              ...(canViewAdjustments ? [<Tab key="ajustes">{tw.ajusteTab}</Tab>] : []),
            ]}
          </TabList>
          <TabPanels>
            {/* -------- Worklist -------- */}
            <TabPanel>
              <Card className="mt-4 flex flex-wrap gap-4 items-end">
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.store}</div>
                  <StoreFilter stores={stores} value={sid} onChange={setSid} />
                </label>
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.from}</div>
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                    className="rounded border border-tremor-border px-3 py-2 bg-tremor-background text-tremor-content-strong" />
                </label>
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.to}</div>
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                    className="rounded border border-tremor-border px-3 py-2 bg-tremor-background text-tremor-content-strong" />
                </label>
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.cause}</div>
                  <Select value={cause} onValueChange={setCause} className="w-48">
                    <SelectItem value="">{tw.filters.allCauses}</SelectItem>
                    <SelectItem value="not-discounted">{tw.causes.notDiscounted}</SelectItem>
                    <SelectItem value="wrong-amount">{tw.causes.wrongAmount}</SelectItem>
                    <SelectItem value="commit-only">{tw.causes.commitOnly}</SelectItem>
                  </Select>
                </label>
                <div className="ml-auto flex gap-2">
                  <Button variant="secondary" disabled={!rows || rows.length === 0} onClick={exportPdf}>
                    {tw.downloadPdf}
                  </Button>
                  <Button variant="secondary" disabled={!rows || rows.length === 0} onClick={exportCsv}>
                    CSV
                  </Button>
                </div>
              </Card>

              {err && <ErrorRetry message={err} onRetry={load} />}
              {loading && !rows && <TableSkeleton rows={8} cols={9} />}
              {rows && rows.length === 0 && !loading && (
                <Card className="mt-4"><Text>{tw.empty}</Text></Card>
              )}
              {rows && rows.length > 0 && (
                <Card className="mt-4 p-0 overflow-hidden">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{tw.cols.date}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.store}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.ticket}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.part}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.qty}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.instkBefore}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.instkAfter}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.expected}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.commit}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.onord}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.cause}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.cashier}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.seller}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pgAudit.pageRows.map((r) => (
                        <TableRow key={`${r.sid}-${r.tnum}-${r.line}-${r.part}-${r.ctime}`}>
                          <TableCell className="whitespace-nowrap text-xs">{r.cdate}<br />{r.ctime}</TableCell>
                          <TableCell>{r.sid}</TableCell>
                          <TableCell>
                            <button type="button" onClick={() => openDrill({ kind: 'tkt', sid: r.sid, tnum: r.tnum })}
                              className="text-tremor-brand hover:underline font-mono">
                              {r.tnum}
                            </button>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>
                              {r.line}/{r.part}
                            </DrillCell>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.qtysold}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.instk_before}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{r.instk_after}</TableCell>
                          <TableCell className="text-right tabular-nums text-tremor-content">{r.expected_after}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs text-tremor-content">
                            {r.commitqty_before}→{r.commitqty_after}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.onord}</TableCell>
                          <TableCell>{causeBadge(r.cause)}</TableCell>
                          <TableCell>{r.tkt_id}</TableCell>
                          <TableCell>{r.slid}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination {...pgAudit} className="px-4" />
                </Card>
              )}
              {rows && rows.length > 0 && (
                <Text className="mt-2 text-tremor-content text-xs">{rows.length} {tw.rowsLabel}</Text>
              )}
            </TabPanel>

            {/* -------- Reconciliación -------- */}
            <TabPanel>
              <Card className="mt-4 flex flex-wrap gap-4 items-end">
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.store}</div>
                  <StoreFilter stores={stores} value={reconSid} onChange={setReconSid} />
                </label>
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.date}</div>
                  <input type="date" value={reconDate} onChange={(e) => setReconDate(e.target.value)}
                    className="rounded border border-tremor-border px-3 py-2 bg-tremor-background text-tremor-content-strong" />
                </label>
                <Button disabled={reconSid == null || reconLoading} onClick={runRecon}>
                  {tw.filters.search}
                </Button>
              </Card>
              <Text className="mt-2 text-tremor-content text-sm">{tw.reconHint}</Text>

              {reconErr && <ErrorRetry message={reconErr} onRetry={runRecon} />}
              {reconLoading && <TableSkeleton rows={6} cols={5} />}
              {reconRows && reconRows.length === 0 && !reconLoading && (
                <Card className="mt-4"><Text>{tw.empty}</Text></Card>
              )}
              {reconRows && reconRows.length > 0 && (
                <>
                  <Text className="mt-4 text-sm font-medium">
                    {uncaptured} {tw.uncapturedCount}
                  </Text>
                  <Card className="mt-2 p-0 overflow-hidden">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>{tw.cols.ticket}</TableHeaderCell>
                          <TableHeaderCell>{tw.cols.part}</TableHeaderCell>
                          <TableHeaderCell className="text-right">{tw.cols.qty}</TableHeaderCell>
                          <TableHeaderCell>{tw.cols.cashier}</TableHeaderCell>
                          <TableHeaderCell>{tw.cols.seller}</TableHeaderCell>
                          <TableHeaderCell>{tw.cols.captured}</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pgRecon.pageRows.map((r) => (
                          <TableRow key={`${r.tnum}-${r.line}-${r.part}`}
                            className={!r.captured ? 'bg-red-50 dark:bg-red-950/30' : ''}>
                            <TableCell>
                              <button type="button" onClick={() => openDrill({ kind: 'tkt', sid: reconSid ?? 0, tnum: r.tnum })}
                                className="text-tremor-brand hover:underline font-mono">{r.tnum}</button>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{r.line}/{r.part}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.qtysold}</TableCell>
                            <TableCell>{r.tkt_id}</TableCell>
                            <TableCell>{r.slid}</TableCell>
                            <TableCell>
                              {r.captured
                                ? <Badge color="green">✓</Badge>
                                : <Badge color="red">{tw.uncaptured}</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Pagination {...pgRecon} className="px-4" />
                  </Card>
                </>
              )}
            </TabPanel>

            {/* -------- Comprometido (commit-drift, #294) -------- */}
            <TabPanel>
              <Card className="mt-4 flex flex-wrap gap-4 items-end">
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.store}</div>
                  <StoreFilter stores={stores} value={commitSid} onChange={setCommitSid} />
                </label>
                <Button disabled={commitLoading} onClick={runCommitDrift}>{tw.commitSearch}</Button>
              </Card>
              <Text className="mt-2 text-tremor-content text-sm">{tw.commitHint}</Text>

              {commitErr && <ErrorRetry message={commitErr} onRetry={runCommitDrift} />}
              {commitLoading && <TableSkeleton rows={6} cols={7} />}
              {commitRows && commitRows.length === 0 && !commitLoading && (
                <Card className="mt-4"><Text>{tw.commitEmpty}</Text></Card>
              )}
              {commitRows && commitRows.length > 0 && (
                <Card className="mt-4 p-0 overflow-hidden">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{tw.commitCols.part}</TableHeaderCell>
                        <TableHeaderCell>{tw.commitCols.desc}</TableHeaderCell>
                        <TableHeaderCell>{tw.commitCols.store}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.commitCols.expected}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.commitCols.actual}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.commitCols.drift}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.commitCols.tickets}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {commitRows.flatMap((r) => {
                        const key = `${r.sid}-${r.line}-${r.part}`;
                        const open = commitExpanded === key;
                        const mainRow = (
                          <TableRow key={key} className={r.drift > 0 ? 'bg-red-50 dark:bg-red-950/30' : ''}>
                            <TableCell className="font-mono text-xs">
                              <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>{r.line}/{r.part}</DrillCell>
                            </TableCell>
                            <TableCell className="text-xs">{r.pdesc}</TableCell>
                            <TableCell>{r.store_name}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.expected_commit}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.actual_commit}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">
                              {r.drift > 0
                                ? <Badge color="red">+{r.drift}</Badge>
                                : <span className="text-tremor-content">{r.drift}</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <button type="button" className="text-tremor-brand hover:underline text-xs"
                                onClick={() => setCommitExpanded(open ? null : key)}>
                                {r.open_tickets.length} {open ? '▾' : '▸'}
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                        const detailRows = open
                          ? r.open_tickets.map((tk) => (
                              <TableRow key={`${key}-${tk.tnum}`} className="bg-tremor-background-muted">
                                <TableCell colSpan={2} className="pl-8 text-xs font-mono">
                                  <button type="button" onClick={() => openDrill({ kind: 'tkt', sid: r.sid, tnum: tk.tnum })}
                                    className="text-tremor-brand hover:underline">#{tk.tnum}</button>
                                </TableCell>
                                <TableCell className="text-xs">{tk.slid}</TableCell>
                                <TableCell colSpan={2} className="text-xs">{tk.cdate}</TableCell>
                                <TableCell className="text-right tabular-nums text-xs">{tk.qtysold}</TableCell>
                                <TableCell className="text-right">
                                  {canHeal && r.drift > 0 && (
                                    <Button size="xs" variant="secondary"
                                      onClick={() => setConfirmResync({ sid: tk.store_sid, tnum: tk.tnum, part: `${r.line}/${r.part}`, multi: r.open_tickets.length > 1 })}>
                                      {tw.commitCorregir}
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          : [];
                        return [mainRow, ...detailRows];
                      })}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabPanel>

            {/* -------- Detectadas (Task 11: reconciled discrepancy residuals) -------- */}
            <TabPanel>
              <Card className="mt-4 flex flex-wrap gap-4 items-end">
                <label className="text-sm">
                  <div className="text-tremor-content text-xs mb-1">{tw.filters.store}</div>
                  <StoreFilter stores={stores} value={detSid} onChange={setDetSid} />
                </label>
                <Button disabled={detLoading} onClick={runDetected}>{tw.filters.search}</Button>
              </Card>
              <Text className="mt-2 text-tremor-content text-sm">{tw.detectadasHint}</Text>

              {detErr && <ErrorRetry message={detErr} onRetry={runDetected} />}
              {detLoading && !detRows && <TableSkeleton rows={6} cols={9} />}
              {detRows && detRows.length === 0 && !detLoading && (
                <Card className="mt-4"><Text>{tw.empty}</Text></Card>
              )}
              {detRows && detRows.length > 0 && (
                <Card className="mt-4 p-0 overflow-hidden">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{tw.cols.store}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.part}</TableHeaderCell>
                        <TableHeaderCell>{tw.commitCols.desc}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.colVendido}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.colMovido}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.colResidual}</TableHeaderCell>
                        <TableHeaderCell>{tw.cols.cause}</TableHeaderCell>
                        <TableHeaderCell>{tw.colOrigen}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{tw.cols.ticket}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detRows.flatMap((r) => {
                        const key = `${r.inv_sid}-${r.line}-${r.part}`;
                        const open = detExpanded === key;
                        const mainRow = (
                          <TableRow key={key}>
                            <TableCell>{r.store}</TableCell>
                            <TableCell className="font-mono text-xs">
                              <DrillCell target={{ kind: 'inv', sid: r.inv_sid, line: r.line, part: r.part }}>{r.line}/{r.part}</DrillCell>
                            </TableCell>
                            <TableCell className="text-xs">{r.pdesc}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.sold}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.moved}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{residualBadge(r.residual)}</TableCell>
                            <TableCell>{causeBadge(r.cause)}</TableCell>
                            <TableCell>{origenChip(r)}</TableCell>
                            <TableCell className="text-right">
                              <button type="button" className="text-tremor-brand hover:underline text-xs"
                                onClick={() => setDetExpanded(open ? null : key)}>
                                {r.tickets.length} {open ? '▾' : '▸'}
                              </button>
                              {canAdjust && (
                                <Button size="xs" variant="secondary" className="ml-2"
                                  onClick={async () => {
                                    // Suggested target = system-expected on-hand = current
                                    // on-hand minus the residual, so the resulting audit
                                    // delta is exactly -residual and closeLoop auto-clears
                                    // this row once the next ingest tick reads it back.
                                    const onHand = await fetchInvOnhand(r.inv_sid, r.line, r.part).catch(() => 0);
                                    const suggested = onHand - r.residual;
                                    setAdjustTarget({ sid: r.inv_sid, line: r.line, part: r.part, onHand, residual: r.residual, suggested });
                                    setAdjustQty(suggested);
                                    setAdjustReason('');
                                  }}>
                                  {tw.ajustar}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                        const detailRows = open
                          ? r.tickets.map((tk) => (
                              <TableRow key={`${key}-${tk.tnum}`} className="bg-tremor-background-muted">
                                <TableCell colSpan={2} className="pl-8 text-xs font-mono">
                                  {/* fix-round-2 Minor 3: the ticket's OWN store (tk.ticket_sid), not
                                      r.inv_sid — a satellite ticket can sell against a central
                                      warehouse's inventory, so opening the modal with inv_sid would
                                      point handle-ticket at the wrong store for that exact case. */}
                                  <button type="button" onClick={() => openDrill({ kind: 'tkt', sid: tk.ticket_sid, tnum: tk.tnum })}
                                    className="text-tremor-brand hover:underline">#{tk.tnum}</button>
                                </TableCell>
                                <TableCell colSpan={2} className="text-xs">{tk.buyer}</TableCell>
                                <TableCell className="text-xs">{tk.slid}</TableCell>
                                <TableCell className="text-xs">{tk.ctrmn}</TableCell>
                                <TableCell colSpan={2} className="text-xs">{tk.inv_time}</TableCell>
                                <TableCell />
                              </TableRow>
                            ))
                          : [];
                        return [mainRow, ...detailRows];
                      })}
                    </TableBody>
                  </Table>
                </Card>
              )}
              {detRows && detRows.length > 0 && (
                <Text className="mt-2 text-tremor-content text-xs">{detRows.length} {tw.rowsLabel}</Text>
              )}
            </TabPanel>

            {/* -------- Movimientos de existencia (on-hand change log, trigger design 2026-08-25) -------- */}
            {canChangelog && (
              <TabPanel>
                <Card className="mt-4 space-y-3">
                  <DateRangePresets from={chgFrom} to={chgTo}
                    onChange={({ from: f, to: t2 }) => { setChgFrom(f); setChgTo(t2); }} />
                  <div className="flex flex-wrap gap-4 items-end">
                    <label className="text-sm">
                      <div className="text-tremor-content text-xs mb-1">{tw.filters.store}</div>
                      <StoreFilter stores={stores} value={chgSid} onChange={setChgSid} />
                    </label>
                    <label className="text-sm">
                      <div className="text-tremor-content text-xs mb-1">{tw.changelogFilters.line}</div>
                      <TextInput value={chgLine} onValueChange={setChgLine} placeholder="GON" className="w-28" />
                    </label>
                    <label className="text-sm">
                      <div className="text-tremor-content text-xs mb-1">{tw.changelogFilters.part}</div>
                      <TextInput value={chgPart} onValueChange={setChgPart} placeholder="X1" className="w-28" />
                    </label>
                    <div className="ml-auto flex gap-2">
                      <Button variant="secondary" disabled={!chgRows || chgRows.length === 0} onClick={exportChangelogPdf}>
                        {tw.downloadPdf}
                      </Button>
                      <Button variant="secondary" disabled={!chgRows || chgRows.length === 0} onClick={exportChangelogCsv}>
                        CSV
                      </Button>
                    </div>
                  </div>
                </Card>

                {chgErr && <ErrorRetry message={chgErr} onRetry={loadChangelog} />}
                {chgLoading && !chgRows && <TableSkeleton rows={8} cols={9} />}
                {chgRows && chgRows.length === 0 && !chgLoading && (
                  <Card className="mt-4"><Text>{tw.changelogEmpty}</Text></Card>
                )}
                {chgRows && chgRows.length > 0 && (
                  <Card className="mt-4 p-0 overflow-hidden">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>{tw.changelogCols.store}</TableHeaderCell>
                          <TableHeaderCell>{tw.changelogCols.line}</TableHeaderCell>
                          <TableHeaderCell>{tw.changelogCols.part}</TableHeaderCell>
                          <TableHeaderCell className="text-right">{tw.changelogCols.instk}</TableHeaderCell>
                          <TableHeaderCell className="text-right">{tw.changelogCols.commitqty}</TableHeaderCell>
                          <TableHeaderCell className="text-right">{tw.changelogCols.delta}</TableHeaderCell>
                          <TableHeaderCell>{tw.changelogCols.userid}</TableHeaderCell>
                          <TableHeaderCell>{tw.changelogCols.date}</TableHeaderCell>
                          <TableHeaderCell>{tw.changelogCols.time}</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pgChg.pageRows.map((r) => (
                          <TableRow key={`${r.sid}-${r.line}-${r.part}-${r.cdate}-${r.ctime}`}>
                            <TableCell>{r.sid}</TableCell>
                            <TableCell className="text-xs">{r.line}</TableCell>
                            <TableCell className="font-mono text-xs">
                              <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>
                                {r.part}
                              </DrillCell>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{r.instk}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.commitqty}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{deltaBadge(r.delta)}</TableCell>
                            <TableCell>{r.userid || '—'}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs">{r.cdate}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs">{r.ctime}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Pagination {...pgChg} className="px-4" />
                  </Card>
                )}
                {chgRows && chgRows.length > 0 && (
                  <Text className="mt-2 text-tremor-content text-xs">{chgRows.length} {tw.changelogRowsLabel}</Text>
                )}
              </TabPanel>
            )}

            {/* -------- Ajustes de existencia (Task 7, #209 Half B) --------
                Owner-only tab (adjust:post — see canViewAdjustments),
                matching the server's gate exactly (user decision,
                2026-08-31). The standalone adjust panel additionally
                requires writes_enabled (canAdjust); the transparency log
                below it does not — viewing history shouldn't require
                writes to be on. */}
            {canViewAdjustments && (
              <TabPanel>
                {canAdjust && (
                  <Card className="mt-4 space-y-3">
                    <Title className="text-base">{tw.ajusteTitle}</Title>
                    <Text className="text-sm text-tremor-content">{tw.ajusteHint}</Text>
                    <div className="flex flex-wrap gap-4 items-end">
                      <label className="text-sm">
                        <div className="text-tremor-content text-xs mb-1">{tw.ajusteStore}</div>
                        <StoreFilter stores={stores} value={panelSid} onChange={setPanelSid} />
                      </label>
                      <label className="text-sm">
                        <div className="text-tremor-content text-xs mb-1">{tw.ajusteLine}</div>
                        <TextInput value={panelLine} onValueChange={setPanelLine} placeholder="GON" className="w-28" />
                      </label>
                      <label className="text-sm">
                        <div className="text-tremor-content text-xs mb-1">{tw.ajustePart}</div>
                        <TextInput value={panelPart} onValueChange={setPanelPart} placeholder="X1" className="w-28" />
                      </label>
                      <label className="text-sm">
                        <div className="text-tremor-content text-xs mb-1">{tw.ajusteNewQty}</div>
                        <NumberInput value={panelQty} onValueChange={(n) => setPanelQty(Number.isFinite(n) ? n : 0)} className="w-28" />
                      </label>
                      <label className="text-sm">
                        <div className="text-tremor-content text-xs mb-1">{tw.ajusteReason}</div>
                        {reasonCodes.length > 0 ? (
                          <Select value={panelReason} onValueChange={setPanelReason} className="w-48">
                            <SelectItem value="">{tw.ajusteReasonNone}</SelectItem>
                            {reasonCodes.map((rc) => (
                              <SelectItem key={rc.code} value={rc.code}>{rc.label}</SelectItem>
                            ))}
                          </Select>
                        ) : (
                          <TextInput value={panelReason} onValueChange={setPanelReason} className="w-48" />
                        )}
                      </label>
                      <Button
                        disabled={panelSid == null || !panelLine || !panelPart}
                        onClick={() => {
                          setAdjustTarget({
                            sid: panelSid as number, line: panelLine, part: panelPart,
                            onHand: null, residual: null, suggested: panelQty,
                          });
                          setAdjustQty(panelQty);
                          setAdjustReason(panelReason);
                        }}>
                        {tw.ajustar}
                      </Button>
                    </div>
                  </Card>
                )}

                {adjustSuccessMsg && (
                  <Text className="mt-4 text-sm text-green-600 dark:text-green-400">{adjustSuccessMsg}</Text>
                )}

                <Title className="text-base mt-4">{tw.ajustesLogTitle}</Title>

                {adjErr && <ErrorRetry message={adjErr} onRetry={loadAdjustments} />}
                {adjLoading && !adjRows && <TableSkeleton rows={6} cols={7} />}
                {adjRows && adjRows.length === 0 && !adjLoading && (
                  <Card className="mt-4"><Text>{tw.empty}</Text></Card>
                )}
                {adjRows && adjRows.length > 0 && (
                  <Card className="mt-4 p-0 overflow-hidden">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>{tw.cols.store}</TableHeaderCell>
                          <TableHeaderCell>{tw.cols.part}</TableHeaderCell>
                          <TableHeaderCell className="text-right">{tw.colAnterior}</TableHeaderCell>
                          <TableHeaderCell className="text-right">{tw.colNuevo}</TableHeaderCell>
                          <TableHeaderCell>{tw.colWho}</TableHeaderCell>
                          <TableHeaderCell>{tw.colWhen}</TableHeaderCell>
                          <TableHeaderCell>{tw.colMotivo}</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pgAdj.pageRows.map((r) => (
                          <TableRow key={`${r.sid}-${r.line}-${r.part}-${r.cdate}-${r.ctime}`}>
                            <TableCell>{r.sid}</TableCell>
                            <TableCell className="font-mono text-xs">
                              <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>{r.line}/{r.part}</DrillCell>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{r.old_int}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{r.new_int}</TableCell>
                            <TableCell>{r.by || '—'}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs">{r.cdate}</TableCell>
                            <TableCell className="text-xs">{r.reason || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Pagination {...pgAdj} className="px-4" />
                  </Card>
                )}
                {adjRows && adjRows.length > 0 && (
                  <Text className="mt-2 text-tremor-content text-xs">{adjRows.length} {tw.rowsLabel}</Text>
                )}
              </TabPanel>
            )}
          </TabPanels>
        </TabGroup>
      </main>


      {confirmResync && (
        <Modal ariaLabel={tw.commitConfirmTitle} role="alertdialog" size="sm"
          onClose={() => { if (!healing) setConfirmResync(null); }}>
          <div className="p-5 space-y-3">
            <Title className="text-base">{tw.commitConfirmTitle} #{confirmResync.tnum}</Title>
            <Text className="text-sm text-tremor-content font-mono">{confirmResync.part}</Text>
            <Text className="text-sm">{tw.commitConfirmBody}</Text>
            {confirmResync.multi && (
              <Text className="text-sm text-red-600 dark:text-red-400">{tw.commitConfirmMulti}</Text>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" disabled={healing} onClick={() => setConfirmResync(null)}>
                {tw.commitConfirmCancel}
              </Button>
              <Button color="red" loading={healing} onClick={doResync}>{tw.commitConfirmOk}</Button>
            </div>
          </div>
        </Modal>
      )}

      {adjustTarget && (
        <Modal ariaLabel={tw.ajusteConfirmTitle} role="alertdialog" size="sm"
          onClose={() => { if (!adjusting) { setAdjustTarget(null); setAdjustErr(null); } }}>
          <div className="p-5 space-y-3">
            <Title className="text-base">{tw.ajusteConfirmTitle}</Title>
            <Text className="text-sm text-tremor-content font-mono">{adjustTarget.line}/{adjustTarget.part}</Text>
            <Text className="text-sm">{tw.ajusteConfirmBody}</Text>
            {adjustTarget.onHand != null && (
              <Text className="text-sm">
                {adjustTarget.onHand} → <span className="font-semibold">{adjustQty}</span>
              </Text>
            )}
            {adjustTarget.residual != null && adjustTarget.residual !== 0 && (
              <Text className="text-sm text-amber-600 dark:text-amber-400">{tw.ajusteResidualHint}</Text>
            )}
            <label className="text-sm block">
              <div className="text-tremor-content text-xs mb-1">{tw.ajusteNewQty}</div>
              <NumberInput value={adjustQty} onValueChange={(n) => setAdjustQty(Number.isFinite(n) ? n : 0)} />
            </label>
            <label className="text-sm block">
              <div className="text-tremor-content text-xs mb-1">{tw.ajusteReason}</div>
              {reasonCodes.length > 0 ? (
                <Select value={adjustReason} onValueChange={setAdjustReason}>
                  <SelectItem value="">{tw.ajusteReasonNone}</SelectItem>
                  {reasonCodes.map((rc) => (
                    <SelectItem key={rc.code} value={rc.code}>{rc.label}</SelectItem>
                  ))}
                </Select>
              ) : (
                <TextInput value={adjustReason} onValueChange={setAdjustReason} />
              )}
            </label>
            {adjustErr && <Text className="text-sm text-red-600 dark:text-red-400">{adjustErr}</Text>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" disabled={adjusting} onClick={() => { setAdjustTarget(null); setAdjustErr(null); }}>
                {tw.ajusteConfirmCancel}
              </Button>
              <Button color="red" loading={adjusting} onClick={doAdjust}>{tw.ajusteConfirmOk}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
